// Resolves a minified chunk position back to the original source position by
// fetching and parsing the chunk's `.map` file at runtime. Used by FallbackUI
// to enrich the URL with a real `AudioVideoChatView.jsx:7:11`-style location.
//
// Trade-offs (must remain true for this to work):
//   - The `.map` files must be deployed alongside the `.js` files. CRA emits
//     them by default in `build/static/js/`. Verify nginx serves them.
//   - Adds a one-time fetch (~hundreds of KB to a few MB) per crashed chunk.
//     We cap the wait with a 2s timeout and fall back to chunk position if it
//     misses — better to redirect with imperfect info than hang the crashed UI.
//   - In CN, the GFW may throttle the `.map` fetch — we time-bound it (2s)
//     for that exact reason.

import { SourceMapConsumer } from "source-map-js";

const RESOLVE_TIMEOUT_MS = 2000;
const cache = new Map(); // key = mapUrl, value = SourceMapConsumer

const withTimeout = (promise, ms) =>
  new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (!done) resolve(null);
    }, ms);
    promise.then(
      (v) => { done = true; clearTimeout(timer); resolve(v); },
      () => { done = true; clearTimeout(timer); resolve(null); },
    );
  });

const fetchAndParse = async (mapUrl) => {
  if (cache.has(mapUrl)) return cache.get(mapUrl);
  const res = await fetch(mapUrl, { credentials: "omit", cache: "force-cache" });
  if (!res.ok) throw new Error(`map fetch failed: ${res.status}`);
  const raw = await res.json();
  const consumer = new SourceMapConsumer(raw);
  cache.set(mapUrl, consumer);
  return consumer;
};

// Strips a leading `webpack:///` and CRA's leading `./` so the source path
// reads like `src/pages/.../AudioVideoChatView.jsx` not `webpack:///./src/...`.
const cleanSource = (s) => {
  if (!s || typeof s !== "string") return s;
  return s.replace(/^webpack:\/\/[^/]*\//, "").replace(/^\.\//, "");
};

// Takes the captured error (file = full chunk URL, line/col in chunk) and
// returns a Promise of `{source, line, column}` from the original file, or
// null if the map can't be fetched/parsed within the timeout.
export const resolveOriginalPosition = (err) => {
  if (!err || !err.file || !err.line || !/^https?:\/\//.test(err.file)) {
    return Promise.resolve(null);
  }
  const mapUrl = err.file.split("?")[0].split("#")[0] + ".map";
  const work = (async () => {
    const consumer = await fetchAndParse(mapUrl);
    const original = consumer.originalPositionFor({
      line: Number(err.line),
      column: Number(err.col || 0),
    });
    if (!original || !original.source) return null;
    return {
      source: cleanSource(original.source),
      line: original.line,
      column: original.column,
      name: original.name || null,
    };
  })();
  return withTimeout(work, RESOLVE_TIMEOUT_MS);
};
